import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck, CalendarX, Clock, Video } from "lucide-react";
import { usePortalNextSession } from "../../application/usePortalNextSession";
import { formatBookingTime } from "../../../scheduler";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import type { CancellationStatus } from "../../domain/types";

interface NextSessionCardProps {
    className?: string;
}

/** Refusals worth explaining: the others describe a session that is not next. */
const EXPLAINED_REFUSALS: CancellationStatus[] = ["too_late", "disabled", "shared_session"];

/**
 * When the patient is next seen, and the way in — or out.
 *
 * Renders nothing until there is something to say: no Scheduler, nothing
 * booked, or a request that failed all read the same to a patient — an empty
 * card promising a next session is worse than no card.
 *
 * Independent of the booking section on purpose: a therapist who books every
 * session herself keeps self-booking switched off, and her patients still need
 * to know when the next one is.
 *
 * Cancelling asks first, and where it is not possible says why and who to
 * tell, rather than leaving a patient who cannot come with no way to say so.
 */
export default function NextSessionCard({ className = "" }: NextSessionCardProps) {
    const { t, i18n } = useTranslation();
    const { session, cancelling, cancelled, refusal, cancel } = usePortalNextSession();
    const [confirming, setConfirming] = useState(false);

    if (!session) return null;

    const day = session.date.slice(0, 10);
    const dayLabel = new Date(`${day}T12:00:00`).toLocaleDateString(
        i18n.language,
        { weekday: "long", day: "numeric", month: "long" }
    );

    const confirmCancel = async () => {
        await cancel();
        setConfirming(false);
    };

    return (
        <section
            className={`flex flex-col gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 p-5 dark:border-brand-500/30 dark:bg-brand-400/5 ${className}`}
        >
            <h2 className="flex items-center gap-2 text-base font-semibold">
                <CalendarCheck size={18} aria-hidden="true" />
                {t("portal.nextSession.title")}
            </h2>

            <p className="text-lg font-semibold capitalize text-content-primary">
                {dayLabel}
            </p>

            <p className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                <Clock size={14} aria-hidden="true" className="shrink-0" />
                <span>
                    {/* The hour carries its zone, so a patient in another
                        country reads the time their therapist meant. */}
                    {formatBookingTime(day, session.hour, session.timezone)} ·{" "}
                    {session.duration} {t("scheduler.common.minutes")}
                </span>
            </p>

            {cancelled ? (
                <p role="status" className="text-sm font-medium text-content-primary">
                    {t("portal.nextSession.cancelled")}
                </p>
            ) : (
                <>
                    {session.meeting_link ? (
                        <a
                            href={session.meeting_link}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700"
                        >
                            <Video size={16} aria-hidden="true" />
                            {t("portal.nextSession.join")}
                        </a>
                    ) : (
                        // Only an online session is missing something; an in-person
                        // one has nowhere to link to and should not look incomplete.
                        session.ubication === "online" && (
                            <p className="text-xs text-gray-500">
                                {t("portal.nextSession.noLinkYet")}
                            </p>
                        )
                    )}

                    {session.can_cancel ? (
                        <button
                            type="button"
                            onClick={() => setConfirming(true)}
                            className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 text-sm font-medium text-content-secondary hover:bg-surface-sunken dark:border-gray-700"
                        >
                            <CalendarX size={16} aria-hidden="true" />
                            {t("portal.nextSession.cancel")}
                        </button>
                    ) : (
                        EXPLAINED_REFUSALS.includes(session.cancellation_status) && (
                            <p className="text-xs text-content-tertiary">
                                {t(`portal.nextSession.cancelRefused.${session.cancellation_status}`)}
                            </p>
                        )
                    )}

                    {refusal && (
                        <p role="alert" className="text-sm text-red-600">
                            {t(refusal)}
                        </p>
                    )}
                </>
            )}

            <ConfirmDialog
                isOpen={confirming}
                destructive
                isBusy={cancelling}
                title={t("portal.nextSession.cancelTitle")}
                message={t("portal.nextSession.cancelMessage")}
                confirmLabel={t("portal.nextSession.cancelConfirm")}
                cancelLabel={t("portal.nextSession.keep")}
                onConfirm={confirmCancel}
                onCancel={() => setConfirming(false)}
            />
        </section>
    );
}
